import React, { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import Callout from "../../ui_atoms/Callout";
import {
    formatCurrency,
    getContractPercentage,
    getContractWeeklyDurationInMinutes,
    getMissionPercentage,
    IjmSelection,
    toFixedIfNecessary,
} from "../../utils";
import Loading from "../Loading";
import { useNavigation } from "@react-navigation/native";
import { Contract, EarningType, Mission, SalaryType } from "../../__generated__/graphql";
import { Buildable, XOR } from "ts-essentials";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { Salaries, useComputeSalary } from "./salary/hooks";
import SalaryBreakdown from "./salary/SalaryBreakdown";
import SmallModal from "../SmallModal";
import TextField from "../TextField";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import Gap from "../../ui_atoms/Gap";
import Text from "../Text";
import SelectField from "../SelectField";
import { useSetRecoilState } from "recoil";
import { currentClientMissionFeesRate } from "../../contexts/CreateMissionManager";
import { BodySmallText, TitleMediumText } from "../FormattedText";
import DropdownField from "../DropdownField";
import { OptionType } from "../OptionsSelector";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useBuildIjmDescription } from "../../hooks/ijm";
import ButtonField from "../ButtonField";
import { useIJMEditor } from "../../hooks/use-ijm-editor";

const getSalaryInfo = (salaryType: SalaryType, salaryDetails?: Salaries | null) => {
    if (!salaryDetails) {
        return {
            salary: 0,
            minSalary: 0,
        };
    }
    switch (salaryType) {
        case SalaryType.Base:
            return {
                salary: salaryDetails.baseSalary,
                minSalary: salaryDetails.minBaseSalary,
            };
        case SalaryType.Gross:
            return {
                salary: salaryDetails.grossSalary,
                minSalary: salaryDetails.minGrossSalary,
            };
        case SalaryType.Net:
            return {
                salary: salaryDetails.netSalary,
                minSalary: salaryDetails.minNetSalary,
            };
        case SalaryType.Invoice:
            return {
                salary: salaryDetails.invoiceSalary,
                minSalary: salaryDetails.minInvoiceSalary,
            };
        default:
            return {
                salary: 0,
                minSalary: 0,
            };
    }
};

export const useStyles = () => {
    const { gridSize, colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                salaryInfos: {
                    marginBottom: gridSize,
                },
                salaryDetailsItem: { padding: gridSize },
                markdownLink: {
                    fontWeight: "400",
                    color: colors.secondary,
                },
                helpButtonIcon: {
                    margin: 0,
                    marginBottom: -21,
                    position: "relative",
                    right: 2,
                    top: -10,
                },
                calculationText: {},
                calculationTextDetail: {
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 4,
                },
                calculationTextDetailText: {
                    ...fonts.bodyMedium,
                },
                salaryField: { flexShrink: 1, flexGrow: 2, flexBasis: "auto" },
            }),
        [colors, fonts, gridSize],
    );
};

type MissionSalaryProps = {
    mission: Buildable<Mission>;
    ijmSelection?: IjmSelection | null;
    initialSalary?: number;
    setSalaries: (salaries: Salaries) => void;
    setVacationdays: (vacationDays: number) => void;
    setHas13thSalary: (has13thSalary: boolean) => void;
    setTotalHours: (hours: number) => void;
    setIjmSelection: (selection: IjmSelection) => void;
    setLoading?: (loading: boolean) => void;
};

type ScreenParams = StackScreenProps<RootStackParamList>;

export type ModalDataType = {
    title: string;
    titlePrefix?: string;
} & XOR<{ markdown: string }, { contentComponent: React.ReactElement }>;

const getMaxHours = (startDate: Date | null, endDate: Date | null) => {
    const dayMaxHours = 11.5;

    if (!startDate || !endDate) {
        return 28 * dayMaxHours;
    }
    // +1 to include the last day as when we work for just 1 day, start and end date are the same
    const nbDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const nbWeeks = Math.floor(nbDays / 7);
    const remainingDays = nbDays % 7;
    // max 6 days per week
    return nbWeeks * (dayMaxHours * 6) + remainingDays * dayMaxHours;
};

const MissionSalary: React.FC<MissionSalaryProps> = ({
    mission,
    ijmSelection,
    setSalaries,
    initialSalary,
    setHas13thSalary,
    setVacationdays,
    setTotalHours,
    setIjmSelection,
    setLoading,
}) => {
    const { fonts, gridSize, colors } = useTheme();
    const i18n = useI18n();
    const styles = useStyles();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const [salaryInternalValue, setSalaryInternalValue] = useState<string>(
        initialSalary ? (initialSalary / 100).toString() : "",
    );
    const [hoursInternalValue, setHoursInternalValue] = useState<string>(
        mission.currentContract?.workingTimeClassicSpec?.unitCount?.toString() || "",
    );
    const [salaryValue, setSalaryValue] = useState<string>();
    const [isSalaryTooLow, setIsSalaryTooLow] = useState(false);
    const [salaryType, setSalaryType] = useState<SalaryType>(SalaryType.Gross);

    const contractPercentage = useMemo(
        () => getContractPercentage(mission.currentContract as Contract) || 0,
        [mission.currentContract],
    );
    const setCurrentClientMissionFeesRate = useSetRecoilState(currentClientMissionFeesRate);
    const startDate = useMemo(
        () =>
            mission.currentContract?.startDate ? new Date(mission.currentContract.startDate) : null,
        [mission.currentContract?.startDate],
    );
    const endDate = useMemo(
        () => (mission.currentContract?.endDate ? new Date(mission.currentContract.endDate) : null),
        [mission.currentContract?.endDate],
    );

    const handleSetSalaries = React.useCallback(
        (salaries: Salaries | null) => {
            let newSalaryTooLow;
            if (!salaries) {
                setSalaries({
                    requestedSalaryType: salaryType,
                    invoiceSalary: 0,
                    netSalary: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
                return;
            }
            const { salary, minSalary } = getSalaryInfo(salaryType, salaries);
            //everytime we receive a computed salary we update the field in case salary has been updated
            if (mission.currentContract?.earningType === EarningType.Monthly) {
                if (contractPercentage) {
                    newSalaryTooLow = (minSalary * contractPercentage) / 100 > salary;
                } else {
                    newSalaryTooLow = false;
                }
            } else {
                newSalaryTooLow = minSalary > salary;
            }

            setIsSalaryTooLow(newSalaryTooLow);
            if (!newSalaryTooLow) {
                setSalaries(salaries);
            } else {
                setSalaries({
                    requestedSalaryType: SalaryType.Gross,
                    invoiceSalary: 0,
                    netSalary: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
            }
            let adjustedSalaryValue = 0;
            switch (salaries.requestedSalaryType) {
                case SalaryType.Gross:
                    adjustedSalaryValue = salaries.grossSalary;
                    break;
                case SalaryType.Net:
                    adjustedSalaryValue = salaries.netSalary;
                    break;
                case SalaryType.Invoice:
                    adjustedSalaryValue = salaries.invoiceSalary;
                    break;
                case SalaryType.Base:
                    adjustedSalaryValue = salaries.baseSalary;
                    break;
            }
            setSalaryInternalValue((adjustedSalaryValue / 100).toString());
        },
        [setSalaries, mission.currentContract?.earningType, contractPercentage, salaryType],
    );

    const { computeSalary, salaryDetails, loading, error } = useComputeSalary(
        mission as Mission,
        ijmSelection,
        handleSetSalaries,
        setVacationdays,
        setHas13thSalary,
    );

    useEffect(() => {
        // used to disabled buttons while loading in upper component.
        setLoading?.(loading);
    }, [setLoading, loading]);

    React.useEffect(() => {
        setCurrentClientMissionFeesRate(
            salaryDetails?.getSalaryDetails.clientMissionFeesRate || "0",
        );
    }, [salaryDetails, setCurrentClientMissionFeesRate]);

    const contractEarningType = useMemo(
        () => mission.currentContract?.earningType || EarningType.Monthly,
        [mission.currentContract?.earningType],
    );

    React.useEffect(
        () => {
            const parsedSalaryValue = parseFloat(salaryInternalValue);
            let noInfo: boolean = !parsedSalaryValue;
            if (
                contractEarningType === EarningType.Package &&
                (mission.currentContract?.workingTimeClassicSpec?.unitCount || 0) <= 0
            ) {
                noInfo = true;
            }
            if (noInfo) {
                void computeSalary(0, salaryType);
            } else {
                void computeSalary(parsedSalaryValue, salaryType);
            }
            // in this case we want to react only on salaryValue change so that the
            // salaryInternalValue is only used as data source of the TextInput
        },
        [salaryValue, salaryType, contractEarningType, computeSalary], //eslint-disable-line react-hooks/exhaustive-deps
    );

    const [modalData, setModalData] = useState<ModalDataType | null>(null);

    const vacationDaysItems = useMemo(() => {
        const minVacationDays = salaryDetails?.getSalaryDetails.minVacationDays || 20;
        return new Array(41 - minVacationDays).fill(0).map((_, index) => {
            const days = index + minVacationDays;
            const fullWeek = days % 5 === 0;
            const nbWeeks = Math.floor(days / 5);
            return {
                label: i18n.t(
                    `screens.user.create_mission_salary.vacation_days${
                        fullWeek ? "_full_week" : ""
                    }_format`,
                    {
                        days,
                        nbWeeks,
                    },
                ),
                value: days,
            };
        });
    }, [i18n, salaryDetails?.getSalaryDetails.minVacationDays]);

    const onTextFieldChange = useCallback(
        (text: string) => {
            setSalaryInternalValue((old) => {
                if (old !== text) {
                    // reset salary values to disable the "next" button
                    setSalaries({
                        requestedSalaryType: salaryType,
                        invoiceSalary: 0,
                        netSalary: 0,
                        grossSalary: 0,
                        baseSalary: 0,
                        minBaseSalary: 0,
                        minGrossSalary: 0,
                        minNetSalary: 0,
                        minInvoiceSalary: 0,
                    });
                }
                return text;
            });
        },
        [salaryType, setSalaries],
    );

    const onHoursFieldChange = useCallback(
        (text: string) => {
            setHoursInternalValue((old) => {
                if (old !== text) {
                    setSalaries({
                        requestedSalaryType: salaryType,
                        invoiceSalary: 0,
                        netSalary: 0,
                        grossSalary: 0,
                        baseSalary: 0,
                        minBaseSalary: 0,
                        minGrossSalary: 0,
                        minNetSalary: 0,
                        minInvoiceSalary: 0,
                    });
                }
                return text;
            });
        },
        [salaryType, setSalaries],
    );

    const formatDuration = useDurationFormatter();
    const workingWeeklyDurationInMinutes = useMemo(() => {
        return getContractWeeklyDurationInMinutes(mission.currentContract as Contract);
    }, [mission]);

    const salaryTypeData = useMemo(
        () =>
            [
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.gross"),
                    value: SalaryType.Gross,
                },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.net"),
                    value: SalaryType.Net,
                },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.invoice"),
                    value: SalaryType.Invoice,
                },
            ] as [OptionType<SalaryType>, OptionType<SalaryType>, OptionType<SalaryType>],
        [i18n],
    );

    const { minSalary } = useMemo(() => {
        return getSalaryInfo(salaryType, salaryDetails?.getSalaryDetails);
    }, [salaryDetails?.getSalaryDetails, salaryType]);

    const maxHours = useMemo(() => {
        return getMaxHours(startDate, endDate);
    }, [endDate, startDate]);

    const onChangeHours = useCallback(() => {
        const val = parseFloat(hoursInternalValue);
        if (!isNaN(val)) {
            if (val > maxHours) {
                setHoursInternalValue(maxHours.toString());
                setTotalHours(val);
            }
            setTotalHours(val);
        }
    }, [hoursInternalValue, setTotalHours, maxHours]);

    const verifyMaxHours = useCallback(
        (start: Date | null, end: Date | null) => {
            const nbHours = getMaxHours(start, end);
            setHoursInternalValue((old) => {
                if (parseFloat(old) > nbHours) {
                    setTotalHours(nbHours);
                    return nbHours.toString();
                }
                return old;
            });
        },
        [setTotalHours],
    );

    useEffect(() => {
        verifyMaxHours(startDate, endDate);
    }, [startDate, endDate, verifyMaxHours]);

    const salaryMaxValue = useMemo(() => {
        switch (contractEarningType) {
            case EarningType.Monthly:
                return 50_000;
            case EarningType.Daily:
                return 10_000;
            case EarningType.Hourly:
                return 5_000;
            case EarningType.Package:
                return 1_000_000;
            default:
                return 0;
        }
    }, [contractEarningType]);

    const isDisplayVacationPercentage = useMemo(() => {
        return (
            mission.currentContract?.earningType === EarningType.Monthly &&
            contractPercentage !== 100
        );
    }, [contractPercentage, mission.currentContract?.earningType]);

    const currentProfile = useCurrentProfile();
    const canChangeIjmGroup = useMemo(() => {
        return currentProfile?.client?.canChangeIjmGroup;
    }, [currentProfile?.client?.canChangeIjmGroup]);
    const buildIjmDescription = useBuildIjmDescription(" • ");

    const editIjm = useIJMEditor();
    const doEditIjm = useCallback(() => {
        editIjm(
            {
                currentSelection: ijmSelection,
                salaryInfo:
                    mission.currentContract?.grossSalary && mission.currentContract?.earningType
                        ? {
                              amount: mission.currentContract.grossSalary,
                              earningType: mission.currentContract.earningType,
                          }
                        : undefined,
            },
            (selection) => {
                if (selection) {
                    setIjmSelection(selection);
                }
            },
        );
    }, [
        editIjm,
        ijmSelection,
        mission.currentContract?.earningType,
        mission.currentContract?.grossSalary,
        setIjmSelection,
    ]);

    return (
        <>
            <SmallModal
                visible={!!modalData}
                titlePrefix={modalData?.titlePrefix}
                title={modalData?.title || ""}
                onClose={() => {
                    setModalData(null);
                }}
            >
                {modalData?.markdown ? (
                    <SmallModal.Markdown markdown={modalData.markdown} />
                ) : modalData?.contentComponent ? (
                    cloneElement(modalData.contentComponent)
                ) : null}
            </SmallModal>

            <VStack>
                <VStack>
                    {contractEarningType !== EarningType.Package && <Gap gap={1 / 2} />}
                    {contractEarningType === EarningType.Package && (
                        <VStack>
                            <Gap gap={1 / 2} />
                            <BodySmallText>
                                {i18n.t(
                                    "screens.user.create_mission_salary_package.requested_salary_title",
                                )}
                            </BodySmallText>
                            <Gap gap={1 / 2} />
                        </VStack>
                    )}
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <TextField
                            selectTextOnFocus
                            disabled={loading}
                            style={{ flexShrink: 1, flexGrow: 1, flexBasis: "auto" }}
                            maxValue={salaryMaxValue}
                            inputStyle={{ ...fonts.headlineMedium, textAlign: "right" }}
                            label={i18n.t(
                                `screens.user.create_mission_salary_${contractEarningType}.requested_salary_label`,
                            )}
                            isDecimalNumber={true}
                            value={salaryInternalValue}
                            onBlur={() => {
                                if (salaryInternalValue !== salaryValue) {
                                    setSalaryValue(salaryInternalValue);
                                }
                            }}
                            onChangeText={onTextFieldChange}
                            keyboardType="decimal-pad"
                            returnKeyType="done"
                            left="CHF"
                            leftColor={colors.greyAccessible}
                            labelLeftPadding={8}
                        />
                        <Gap gap={1 / 2} />
                        <DropdownField<SalaryType>
                            numberOfLines={1}
                            style={{ flexShrink: 1, flexGrow: 1, flexBasis: "75%" }}
                            items={salaryTypeData}
                            onSelect={setSalaryType}
                            value={salaryType}
                        />
                    </View>
                    <Gap gap={1} />
                    {contractEarningType === EarningType.Monthly &&
                        workingWeeklyDurationInMinutes && (
                            <BodySmallText color={colors.onSurfaceDisabled}>
                                {i18n.t(
                                    `screens.user.create_mission_salary_monthly.working_percentage_info`,
                                    {
                                        percentage: getMissionPercentage(mission as Mission),
                                        duration: formatDuration(workingWeeklyDurationInMinutes),
                                    },
                                )}
                            </BodySmallText>
                        )}
                    {contractEarningType === EarningType.Package && (
                        <>
                            <Gap gap={1 / 2} />
                            <BodySmallText>
                                {i18n.t(
                                    "screens.user.create_mission_salary_package.requested_hours_title",
                                )}
                            </BodySmallText>
                            <Gap gap={1 / 2} />
                            <TextField
                                selectTextOnFocus
                                disabled={loading}
                                maxValue={maxHours}
                                isDecimalNumber={true}
                                value={hoursInternalValue}
                                onBlur={onChangeHours}
                                onChangeText={onHoursFieldChange}
                                keyboardType="decimal-pad"
                                returnKeyType="done"
                                inputStyle={{ textAlign: "right" }}
                            />
                            <BodySmallText color={colors.onSurfaceDisabled}>
                                {i18n.t("screens.user.create_mission_salary_package.max_hours", {
                                    hours: maxHours,
                                })}
                            </BodySmallText>
                        </>
                    )}

                    {/* Vacations INFO */}
                    <Gap gap={1} />
                    <TitleMediumText>
                        {i18n.t("screens.user.create_mission_salary.vacationExplained")}
                    </TitleMediumText>
                    <SelectField
                        style={{
                            marginHorizontal: -((gridSize * 2) / 3) + 2,
                        }}
                        asListAction={
                            mission.currentContract?.vacationDays ? "primary" : "secondary"
                        }
                        onSelect={({ value }) => {
                            if (value) {
                                setVacationdays(value);
                            }
                        }}
                        data={vacationDaysItems}
                        placeholder={i18n.t("screens.user.create_mission_salary.setVacationDays")}
                        value={mission.currentContract?.vacationDays}
                        right="gear"
                        halfScreen
                        info={
                            isDisplayVacationPercentage && (
                                <BodySmallText color={colors.onSurfaceDisabled}>
                                    {i18n.t(
                                        "screens.user.create_mission_salary.vacation_percentage",
                                        {
                                            percent: contractPercentage,
                                            count: toFixedIfNecessary(
                                                ((mission.currentContract?.vacationDays || 0) *
                                                    contractPercentage) /
                                                    100,
                                                2,
                                            ),
                                        },
                                    )}
                                </BodySmallText>
                            )
                        }
                    />
                    <Gap gap={1.5} />

                    {/* IFM INFO */}
                    <TitleMediumText>
                        {i18n.t("screens.user.create_mission_salary.ijmExplained")}
                    </TitleMediumText>
                    {ijmSelection && ijmSelection.ijmGroupId ? (
                        <ButtonField
                            style={{ marginHorizontal: -((gridSize * 2) / 3) + 2 }}
                            asListAction={canChangeIjmGroup ? "primary" : undefined}
                            text={buildIjmDescription(
                                ijmSelection?.ijmGroupName || undefined,
                                ijmSelection?.ijmPayFirstDays,
                                ijmSelection?.ijmPayRemainingPercents,
                            )}
                            right={canChangeIjmGroup ? "gear" : undefined}
                            onPress={canChangeIjmGroup ? doEditIjm : undefined}
                        />
                    ) : (
                        <ButtonField
                            style={{ marginHorizontal: -((gridSize * 2) / 3) + 2 }}
                            asListAction="secondary"
                            text={i18n.t("screens.user.create_mission_salary.setIjmAction")}
                            right="gear"
                            onPress={doEditIjm}
                        />
                    )}
                    <Gap gap={0.5} />
                </VStack>

                {loading && <Loading />}
                {!loading && error && <Callout markdown={error} mode="error" />}
                {!loading && !error && salaryDetails && ijmSelection && (
                    <>
                        {isSalaryTooLow && (
                            <>
                                <Callout
                                    mode="error"
                                    markdown={i18n.t(
                                        `screens.user.create_mission_salary_${contractEarningType}.min_salary_info_plus`,
                                        {
                                            percentage: contractPercentage,
                                            percentageAmount: formatCurrency({
                                                amountInCents:
                                                    (contractPercentage / 100) * minSalary,
                                                displayDigitsWhenNoCents: false,
                                            }),
                                            amount: formatCurrency({
                                                amountInCents: minSalary,
                                                displayDigitsWhenNoCents: false,
                                            }),
                                            hours: hoursInternalValue,
                                            salaryType: i18n
                                                .t(
                                                    `screens.user.create_mission_salary.salaryType.${salaryType}`,
                                                )
                                                .toLowerCase(),
                                        },
                                    )}
                                />
                                <Gap gap={1 / 2} />
                            </>
                        )}

                        <LoadingBoundary loading={loading}>
                            <Gap gap={1} />
                            <TitleMediumText>
                                {i18n.t("screens.user.create_mission_salary.salary_breakdown")}
                            </TitleMediumText>
                            <Gap gap={0.5} />
                            <SalaryBreakdown
                                earningType={contractEarningType}
                                setModalData={setModalData}
                                salaryDetails={salaryDetails}
                            />
                        </LoadingBoundary>
                        <Gap gap={1.5} />
                        <TouchableOpacity
                            onPress={() => {
                                navigation.navigate("SalaryCountingDetails", {
                                    earningType: contractEarningType,
                                    salaryDetails: salaryDetails.getSalaryDetails,
                                });
                            }}
                        >
                            <Text style={[fonts.bodyMedium, styles.markdownLink]}>
                                {i18n.t("screens.user.create_mission_salary.show_details")}
                            </Text>
                        </TouchableOpacity>
                    </>
                )}
                <Gap gap={0.5} />
            </VStack>
        </>
    );
};

export default MissionSalary;
